@file:OptIn(ExperimentalWasmJsInterop::class)

package org.matthewo.table

import csstype.PropertiesBuilder
import emotion.react.css
import js.objects.buildReadonlyRecord
import org.matthewo.collections.nullsLast
import org.matthewo.styles.BACKGROUND_COLOR
import org.matthewo.styles.INFO_COLOR
import org.matthewo.styles.lighten
import react.*
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.span
import react.dom.html.ReactHTML.table
import react.dom.html.ReactHTML.tbody
import react.dom.html.ReactHTML.td
import react.dom.html.ReactHTML.thead
import react.dom.html.ReactHTML.tr
import react.dom.html.TdHTMLAttributes
import tanstack.react.router.useNavigate
import tanstack.react.router.useSearch
import web.cssom.*
import web.html.HTMLTableCellElement

const val SORT_SEARCH = "sort"
const val ORDER_SEARCH = "order"

enum class Order {
    ASCENDING,
    DESCENDING;

    val value = name.lowercase()

    val reversed: Order
        get() = when (this) {
            ASCENDING -> DESCENDING
            DESCENDING -> ASCENDING
        }

    companion object {
        fun parse(string: String): Order? = entries.firstOrNull { it.value.equals(string, ignoreCase = true) }
    }
}

private data class SortParams(val column: Int, val order: Order)

fun <T> Table(
    pinHeader: Boolean = true,
    pinFirstColumn: Boolean = true,
    sortQuery: Boolean = true,
    initialSortColumn: Int = 0,
    vararg columns: Column<T, Unit>,
) = Table(pinHeader, pinFirstColumn, sortQuery, initialSortColumn, columns.toList())

fun <T> Table(
    pinHeader: Boolean = true,
    pinFirstColumn: Boolean = true,
    sortQuery: Boolean = true,
    initialSortColumn: Int = 0,
    columns: List<Column<T, Unit>>,
) = Table<T, Unit>(pinHeader, pinFirstColumn, sortQuery, initialSortColumn, columns)

fun <T, S> Table(
    pinHeader: Boolean = true,
    pinFirstColumn: Boolean = true,
    sortQuery: Boolean = true,
    initialSortColumn: Int = 0,
    vararg columns: Column<T, S>,
) = Table(pinHeader, pinFirstColumn, sortQuery, initialSortColumn, columns.toList())

fun <T, S> Table(
    pinHeader: Boolean = true,
    pinFirstColumn: Boolean = true,
    sortQuery: Boolean = true,
    initialSortColumn: Int = 0,
    columns: List<Column<T, S>>,
) = FC<TableProps<T, S>>("Table") { props ->
    val searchParams = useSearch()
    val navigate = useNavigate()

    var sorting by useState(SortParams(initialSortColumn, columns[initialSortColumn].defaultOrder))

    if (sortQuery) {
        useEffect(searchParams) {
            val sortColumn = columns.indexOfFirst { it.title == searchParams[SORT_SEARCH] }
            val order = searchParams[ORDER_SEARCH]?.toString()?.let(Order::parse) ?: sorting.order
            sorting =
                if (sortColumn < 0) SortParams(initialSortColumn, sorting.order)
                else SortParams(sortColumn, order)
        }
    }

    val sorted = useMemo(sorting, props.entries) {
        val sorter = columns[sorting.column].sort ?: return@useMemo props.entries
        props.entries.sortedWith(sorter.run { if (sorting.order == DESCENDING) reversed() else this }.nullsLast())
    }

    div {
        css {
            margin = 0.em
        }

        table {
            css {
                width = 100.pct
                fontSize = 1.rem
                borderCollapse = BorderCollapse.collapse
            }

            thead {
                tr {
                    if (pinHeader) {
                        css {
                            position = Position.sticky
                            top = 0.px
                            zIndex = integer(4)
                            backgroundColor = BACKGROUND_COLOR
                        }
                    }
                    columns.withIndex().forEach { (i, column) ->
                        td {
                            css {
                                padding = 0.5.em
                                minWidth = 3.em
                                if (i == 0) {
                                    if (pinFirstColumn) {
                                        background = Globals.inherit
                                        position = Position.sticky
                                        left = 0.px
                                    }
                                    width = 4.em
                                }
                            }
                            if (null != column.sort) {
                                onClick = {
                                    val descending = if (sorting.column == i) sorting.order.reversed else columns[i].defaultOrder
                                    if (sortQuery) {
                                        navigate {
                                            search = buildReadonlyRecord {
                                                this["sort"] = column.title
                                                this["order"] = descending.value
                                            }
                                        }
                                    } else {
                                        sorting = SortParams(i, descending)
                                    }
                                }
                            }
                            span {
                                css {
                                    lineHeight = HEADER_LINE_HEIGHT
                                }
                                span {
                                    css {
                                        textDecoration = TextDecoration.underline
                                    }
                                    +column.title
                                }
                                +when {
                                    sorting.column != i -> ""
                                    sorting.order == DESCENDING -> " ▼"
                                    else -> " ▲"
                                }
                            }
                        }
                    }
                }
            }
            tbody {
                sorted.withIndex().forEach { (i, entry) ->
                    val bright = i % 2 == 0
                    tr {
                        css {
                            padding = array(0.em, 1.em)
                            backgroundColor = BACKGROUND_COLOR.lighten(if (bright) 1.5 else 1.0)
                        }
                        columns.forEachIndexed { j, column ->
                            td {
                                onClick = {
                                    props.onClick(column, entry)
                                }

                                val text = column.display(i, entry)?.toString()
                                if (null == column.content) +(text ?: "")
                                else column.content.invoke(this, props.config, entry, text)

                                css(className) {
                                    if (0 == j && pinFirstColumn) {
                                        position = Position.sticky
                                        left = 0.px
                                        zIndex = integer(2)
                                    }
                                    backgroundColor = column.color.lighten(if (bright) 1.5 else 1.0)
                                    padding = 0.5.em
                                    if (null != column.style) column.style.invoke(this, props.config, entry)
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

external interface TableProps<T, S> : Props {
    var entries: List<T>
    var onClick: (Column<T, S>, T) -> Unit
    var config: S
}

data class Column<T, S>(
    val title: String,
    val display: (Int, T) -> Any?,
    val sort: Comparator<T>?,
    val defaultOrder: Order = DESCENDING,
    val color: Color = INFO_COLOR.tableBody,
    val style: (PropertiesBuilder.(S, T) -> Unit)? = null,
    val content: (@ReactDsl TdHTMLAttributes<HTMLTableCellElement>.(S, T, String?) -> Unit)? = null,
) {
    constructor(
        title: String,
        display: (T) -> Any?,
        sort: Comparator<T>?,
        defaultOrder: Order = DESCENDING,
        color: Color = INFO_COLOR.tableBody,
        style: (PropertiesBuilder.(S, T) -> Unit)? = null,
        content: (@ReactDsl TdHTMLAttributes<HTMLTableCellElement>.(S, T, String?) -> Unit)? = null,
    ) : this(
        title,
        { _, v -> display(v) },
        sort,
        defaultOrder,
        color,
        style,
        content,
    )

    constructor(
        title: String,
        display: (T) -> Any?,
        sort: Comparator<T>?,
        defaultOrder: Order = DESCENDING,
        color: Color = INFO_COLOR.tableBody,
        style: (PropertiesBuilder.(S, T) -> Unit)? = null,
    ) : this(
        title,
        { _, v -> display(v) },
        sort,
        defaultOrder,
        color,
        style,
    )

    companion object {
        operator fun <T> invoke(
            title: String,
            display: (T) -> Any?,
            sort: Comparator<T>?,
            defaultOrder: Order = DESCENDING,
            color: Color = INFO_COLOR.tableBody,
            style: (PropertiesBuilder.(T) -> Unit)? = null,
            content: (@ReactDsl TdHTMLAttributes<HTMLTableCellElement>.(T, String?) -> Unit)? = null,
        ) = Column<T, Unit>(
            title,
            { _, v -> display(v) },
            sort,
            defaultOrder,
            color,
            style?.let { { _, t -> it(t) } },
            content?.let { { _, t, s -> it(t, s) } },
        )

        operator fun <T> invoke(
            title: String,
            display: (T) -> Any?,
            sort: Comparator<T>?,
            defaultOrder: Order = DESCENDING,
            color: Color = INFO_COLOR.tableBody,
            style: (PropertiesBuilder.(T) -> Unit)? = null,
        ) = Column<T, Unit>(
            title,
            { _, v -> display(v) },
            sort,
            defaultOrder,
            color,
            style?.let { { _, t -> it(t) } },
        )
    }
}
